import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { Content } from '@/components/cardWithThreeFooterBox';
import {
  EventTableConfig,
  EventTableConfigModal,
  EventTableType,
  getDefaultTableConfig,
} from '@/components/eventComponent/';
import { GridWidget } from '@/components/GridWidget.tsx';
import { useLayoutProps, useOpenModal } from '@/hooks';
import { ComponentType, GenericWidgetProps } from '@/types.ts';
import { Icon } from '@/utils';
import { EVENT_COMPONENT_NAME } from '@/widgetComponentNames.ts';

import { EventTable } from './eventComponent/EventTable.tsx';
import { InventoryProvider } from './eventComponent/InventoryProvider.tsx';

interface EventComponentProps extends GenericWidgetProps {
  config?: EventTableConfig;
  editable?: boolean;
  widgetId: string;
}

export const EventComponent: ComponentType<EventComponentProps> = ({
  widgetId,
  editable = true,
  config: configInput,
  ...rest
}: EventComponentProps) => {
  const { t } = useTranslation(['common', 'eventStream']);
  const defaultTableConfig = useMemo(() => getDefaultTableConfig(t), [t]);
  const { config } = useLayoutProps<EventTableConfig>({
    configInput,
    defaultConfig: { ...defaultTableConfig, title: t('eventStream:eventStream') },
    widgetId,
  });
  // We know that if widgetId is defined, the widget is in a landing page
  const controllerForModal = useOpenModal();
  const modalController = useMemo(() => controllerForModal, [controllerForModal]);

  const editId = useMemo(() => uuidv4(), []);

  const toolbarActionList = useMemo(() => {
    if (!editable) {
      return undefined;
    }
    return [
      {
        id: editId,
        label: t('common:edit'),
        onClick: modalController.handleOpenModal,
        Icon: Icon.SlidersHorizontal,
      },
    ];
  }, [editId, editable, modalController.handleOpenModal, t]);

  const headerTitle = useMemo(() => {
    const defaultTitle = t('eventStream:eventStream');
    const legacyDefaultTitle = t('eventStream:eventTable');
    const typeBasedTitle =
      config.type === EventTableType.HISTORICAL
        ? t('eventStream:historicalEventStream')
        : t('eventStream:liveEventStream');
    const trimmedTitle = config.title.trim();
    if (
      trimmedTitle === '' ||
      trimmedTitle === defaultTitle ||
      trimmedTitle === legacyDefaultTitle
    ) {
      return typeBasedTitle;
    }
    return config.title;
  }, [config.title, config.type, t]);

  return (
    <InventoryProvider>
      <GridWidget
        {...rest}
        headerTitle={headerTitle}
        widgetId={widgetId}
        toolbarActionList={toolbarActionList}
      >
        <Content $flexDirection={'row'}>
          <EventTableConfigModal
            modalController={modalController}
            widgetId={widgetId}
            defaultTableConfig={defaultTableConfig}
          />
          <EventTable config={config} id={widgetId} defaultTableConfig={defaultTableConfig} />
        </Content>
      </GridWidget>
    </InventoryProvider>
  );
};

EventComponent.componentName = EVENT_COMPONENT_NAME;
